/**
 * React Query hooks for the admin-web content-management screens (006 US10).
 * Query-key factory rooted at ['admin-content', ...]; mutations invalidate the
 * tree and the affected node so the UI catches up immediately after a save.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from '../api/contentApi';
import type {
  CreateEntryRequest,
  UpdateNodeRequest,
  UpsertTranslationRequest,
} from '../api/contentApi';

const contentKeys = {
  all: ['admin-content'] as const,
  tree: () => [...contentKeys.all, 'tree'] as const,
  node: (id: string) => [...contentKeys.all, 'node', id] as const,
};

export function useContentTree() {
  return useQuery({
    queryKey: contentKeys.tree(),
    queryFn: () => api.getContentTree(),
    staleTime: 30_000,
  });
}

export function useContentNode(id: string | undefined) {
  return useQuery({
    queryKey: contentKeys.node(id ?? ''),
    queryFn: () => api.getContentNode(id as string),
    enabled: Boolean(id),
  });
}

export function useUpdateNode() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['admin-content', 'update-node'],
    mutationFn: ({ id, req }: { id: string; req: UpdateNodeRequest }) => api.updateContentNode(id, req),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: contentKeys.tree() });
      qc.invalidateQueries({ queryKey: contentKeys.node(vars.id) });
    },
  });
}

export function useUpsertTranslation() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['admin-content', 'upsert-translation'],
    mutationFn: ({ id, lang, req }: { id: string; lang: string; req: UpsertTranslationRequest }) =>
      api.upsertTranslation(id, lang, req),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: contentKeys.node(vars.id) });
      qc.invalidateQueries({ queryKey: contentKeys.tree() });
    },
  });
}

export function useCreateEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['admin-content', 'create-entry'],
    mutationFn: ({ collectionId, req }: { collectionId: string; req: CreateEntryRequest }) =>
      api.createEntry(collectionId, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: contentKeys.tree() }),
  });
}

export function useReorderEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['admin-content', 'reorder-entry'],
    mutationFn: ({ id, sortOrder }: { id: string; sortOrder: number }) => api.reorderEntry(id, sortOrder),
    onSuccess: () => qc.invalidateQueries({ queryKey: contentKeys.tree() }),
  });
}

export function useDeleteEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['admin-content', 'delete-entry'],
    mutationFn: (id: string) => api.deleteEntry(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: contentKeys.tree() }),
  });
}

export function useUploadAsset() {
  return useMutation({
    mutationKey: ['admin-content', 'upload-asset'],
    mutationFn: (file: File) => api.uploadContentAsset(file),
  });
}
